/**
 * The two preferences that live in BOTH places (`stores/theme.tsx`, `stores/density.ts`).
 *
 * ## Theme and density: localStorage is the fast path, the server is the durable one
 *
 * `110-design-system.md` §2.1 settles this and §4 repeats it: theme and density must be
 * readable **synchronously, before first paint** (the blocking script in `index.html` stamps
 * `data-theme`/`data-density` on `<html>`), which rules out TanStack Query and IndexedDB alike.
 * So localStorage owns the read path. They are *also* persisted to `/api/settings` "so a new
 * browser picks it up".
 *
 * Two directions, and keeping them from fighting each other is the whole content of this file:
 *
 *  - **Local → server.** `useThemePreference()` / `useDensityPreference()` write localStorage
 *    (instant, pre-paint-readable) and enqueue a `settings.patch` (durable). Every control that
 *    changes a theme or a density goes through them — the top bar's cycle button, the palette's
 *    two commands, `Mod+Shift+D`, and the settings screen.
 *  - **Server → local, once.** `<SettingsBridge>` adopts the server's values on the first
 *    successful settings load *of a session*, which is what makes a new browser inherit them.
 *    Guarded by a ref: adopting on every render would mean a local change is immediately
 *    overwritten by the stale cache it just invalidated, and the control would spring back.
 *
 * ## The timezone seed
 *
 * `100-settings.md` §1: `calendar.timezone` has no static default. Fixtures seed it to `UTC`
 * so the row is never missing; on first boot the client detects the browser's zone and PATCHes
 * it **only if the stored value is still the untouched seed**. That guard is not fussiness —
 * it is what stops a laptop taken abroad from silently re-homing the user's whole calendar.
 */
import { useCallback } from 'react';
import { getLang, setLang, subscribe, read, type Language } from '@/lib/i18n';
import { useSyncExternalStore } from 'react';
import { usePatchSettings } from '@/features/settings/useSettings';
import { setDensity as setLocalDensity, useDensity, type Density } from '@/stores/density';
import { useTheme, type Theme } from '@/stores/theme';

export interface ThemePreference {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

/** The theme, written to localStorage AND to `/api/settings`. The only setter any UI may call. */
export function useThemePreference(): ThemePreference {
  const { theme, setTheme: setLocalTheme } = useTheme();
  const patch = usePatchSettings();

  const setTheme = useCallback(
    (next: Theme) => {
      setLocalTheme(next); // instant, and readable before the next paint
      patch({ ui: { theme: next } }); // durable, via the outbox
    },
    [patch, setLocalTheme],
  );

  return { theme, setTheme };
}

export interface DensityPreference {
  density: Density;
  setDensity: (density: Density) => void;
  toggleDensity: () => void;
}

export function useDensityPreference(): DensityPreference {
  const density = useDensity();
  const patch = usePatchSettings();

  const setDensity = useCallback(
    (next: Density) => {
      setLocalDensity(next);
      patch({ ui: { density: next } });
    },
    [patch],
  );

  const toggleDensity = useCallback(() => {
    setDensity(density === 'compact' ? 'comfortable' : 'compact');
  }, [density, setDensity]);

  return { density, setDensity, toggleDensity };
}

export interface LanguagePreference {
  language: Language;
  setLanguage: (language: Language) => void;
}

/**
 * The UI language, written to localStorage (via the i18n store — instant, and re-renders every
 * subscribed component) AND to `/api/settings` (durable, so a new browser inherits it). The only
 * setter any UI may call. Mirrors `useThemePreference` / `useDensityPreference`.
 */
export function useLanguagePreference(): LanguagePreference {
  const language = useSyncExternalStore(subscribe, read, () => 'en' as Language);
  const patch = usePatchSettings();

  const setLanguage = useCallback(
    (next: Language) => {
      setLang(next); // instant; updates <html lang> and re-renders subscribers
      patch({ ui: { language: next } }); // durable, via the outbox
    },
    [patch],
  );

  return { language, setLanguage };
}

// `getLang` is re-exported for non-hook callers that need the current value synchronously.
export { getLang };

/**
 * Mounted once, in the shell. Renders nothing.
 *
 * It runs the two boot-time reconciliations described in the header, and it is deliberately the
 * ONLY place either of them happens.
 */
